import React, { useState, useEffect } from "react";
import Button from "./../../../components/Button/Button";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import {
  getAdherentsByUser,
  updateBlessures,
} from "./../../../services/adherentServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";

const AbsencesPage = () => {
  useScrollToTop();

  const [adherents, setAdherents] = useState([]);
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [isBlessed, setIsBlessed] = useState(false);
  const [dateReprise, setDateReprise] = useState("");
  const [motifAbsence, setMotifAbsence] = useState("");

  const loadData = async () => {
    if (!user || !user.userId) {
      console.error("Utilisateur non identifié");
      return;
    }
    try {
      const adherentsData = await getAdherentsByUser(user.userId);
      setAdherents(adherentsData);
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    if (!selectedAdherentId) return;

    const adherent = adherents.find(
      (a) => a.id === parseInt(selectedAdherentId)
    );
    if (adherent) {
      setIsBlessed(adherent.is_blessed || false);
      setDateReprise(
        adherent.date_reprise ? adherent.date_reprise.split("T")[0] : ""
      );
      setMotifAbsence(""); // On ne pré-remplit pas le motif, car c’est un nouveau à ajouter
    }
  }, [selectedAdherentId, adherents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdherentId) return;

    const adherent = adherents.find(
      (a) => a.id === parseInt(selectedAdherentId)
    );

    const today = new Date().toLocaleDateString("fr-FR");
    const formattedDateReprise = dateReprise
      ? new Date(dateReprise).toLocaleDateString("fr-FR")
      : "non précisée";

    const newNote = `Absence signalée le ${today} jusqu'au ${formattedDateReprise} : ${motifAbsence}`;
    const newNotes = `${
      adherent.notes_adherent ? adherent.notes_adherent + " | " : ""
    }${newNote}`;

    try {
      await updateBlessures(selectedAdherentId, {
        is_blessed: isBlessed,
        date_reprise: dateReprise,
        notes_adherent: newNotes,
      });
      toast.success("Absence signalée avec succès !");
      loadData(); // Recharge les adhérents
    } catch (error) {
      toast.error("Erreur lors de la mise à jour.");
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col w-full mx-auto p-4 mt-[130px] dark:text-white">
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-4">
        UTILISATEURS
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-10 font-montserrat uppercase">
        Signaler une Absence
      </h2>

      <DashboardNavigation role={userRole} />

      {/* Layout 2 colonnes responsive */}
      <div className="flex flex-col-reverse lg:flex-row gap-10 max-w-[1280px] mx-auto items-start">
        {/* Bloc principal */}
        <div className="w-full lg:w-2/3">
          <div className="text-center p-4 bg-blue-100 rounded-lg shadow mb-6 dark:text-gray-800 font-montserrat">
            <p className="text-lg font-semibold mb-2">
              Bienvenue sur la page : Absences / Maladies / Blessures
            </p>
            <p className="text-sm text-left">
              Sur cette page, vous pouvez signaler une absence de votre/vos
              adhérent(s).
              <br />
              Qu'il s'agisse d'une absence pour maladie, blessure ou autre
              raison, vous pouvez le faire ici.
              <br />
              Veuillez choisir l'adhérent concerné et remplir le formulaire pour
              signaler son absence.
            </p>
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 rounded shadow">
            <label className="block mb-2 font-semibold">
              Sélectionner un adhérent :
            </label>
            <select
              className="w-full p-2 border rounded mb-4"
              value={selectedAdherentId}
              onChange={(e) => setSelectedAdherentId(e.target.value)}
            >
              <option value="">-- Choisir un adhérent --</option>
              {adherents.map((adherent) => (
                <option key={adherent.id} value={adherent.id}>
                  {adherent.prenom} {adherent.nom}
                </option>
              ))}
            </select>

            {selectedAdherentId && (
              <>
                {/* Mini-card */}
                <div className="flex items-center mb-4 p-4 border rounded bg-blue-50 dark:bg-blue-900">
                  <img
                    src={
                      adherents.find(
                        (a) => a.id === parseInt(selectedAdherentId)
                      )?.photo_url
                    }
                    alt="photo"
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <p className="font-bold">
                      {
                        adherents.find(
                          (a) => a.id === parseInt(selectedAdherentId)
                        )?.prenom
                      }{" "}
                      {
                        adherents.find(
                          (a) => a.id === parseInt(selectedAdherentId)
                        )?.nom
                      }
                    </p>
                    <p className="text-sm italic text-gray-600 dark:text-gray-300">
                      {
                        adherents.find(
                          (a) => a.id === parseInt(selectedAdherentId)
                        )?.genre
                      }
                    </p>
                  </div>
                </div>

                {/* Formulaire */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isBlessed}
                      onChange={(e) => setIsBlessed(e.target.checked)}
                      id="blessure"
                      className="mr-2"
                    />
                    <label htmlFor="blessure">
                      Blessé(e) / Malade / Absent(e)
                    </label>
                  </div>

                  <div>
                    <label>Date de reprise estimée :</label>
                    <input
                      type="date"
                      className="w-full p-2 border rounded"
                      value={dateReprise}
                      onChange={(e) => setDateReprise(e.target.value)}
                    />
                  </div>

                  {adherents.find((a) => a.id === parseInt(selectedAdherentId))
                    ?.notes_adherent && (
                    <div className="text-sm italic text-gray-600 dark:text-gray-300 mb-2">
                      Notes existantes :{" "}
                      {
                        adherents.find(
                          (a) => a.id === parseInt(selectedAdherentId)
                        ).notes_adherent
                      }
                    </div>
                  )}

                  <div>
                    <label>Motif d’absence :</label>
                    <textarea
                      className="w-full p-2 border rounded"
                      rows="3"
                      value={motifAbsence}
                      onChange={(e) => setMotifAbsence(e.target.value)}
                    />
                  </div>

                  <Button type="submit" text="Enregistrer l’absence" />
                </form>
              </>
            )}
          </div>
        </div>

        {/* Illustration */}
        <div className="w-full lg:w-1/3 flex justify-center items-start">
          <img
            src="./img/blessed.webp"
            alt="Judoka blessé"
            className="w-full h-auto object-contain rounded-xl shadow-lg"
          />
        </div>
      </div>

      <ToastContainer />
    </div>
  );

};

export default AbsencesPage;
